/** E.164 allows up to 15 digits total (including country code). */
export const MAX_E164_DIGITS = 15;

/**
 * Keeps a single leading + and up to MAX_E164_DIGITS digits (spaces etc. stripped).
 */
export function sanitizePhoneInput(raw: string): string {
  let out = "";
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    if (c === "+" && out.length === 0) {
      out = "+";
    } else if (/\d/.test(c)) {
      const digitCount = (out.match(/\d/g) || []).length;
      if (digitCount < MAX_E164_DIGITS) {
        out += c;
      }
    }
  }
  return out;
}

/**
 * Builds E.164: explicit +country…, bare international digits,
 * or Nigerian local 0XXXXXXXXXXX → +234…
 */
export function parseToE164Phone(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const digitsOnly = trimmed.replace(/\D/g, "");

  if (trimmed.startsWith("+")) {
    const d = trimmed.slice(1).replace(/\D/g, "");
    if (d.length < 8 || d.length > MAX_E164_DIGITS) return null;
    return `+${d}`;
  }

  if (digitsOnly.length === 11 && digitsOnly.startsWith("0")) {
    return `+234${digitsOnly.slice(1)}`;
  }

  if (
    digitsOnly.length >= 10 &&
    digitsOnly.length <= MAX_E164_DIGITS &&
    !digitsOnly.startsWith("0")
  ) {
    return `+${digitsOnly}`;
  }

  return null;
}

/** Human-readable preview after parsing (E.164). */
export const formatPhoneNumberPreview = (raw: string): string | null => {
  const e164 = parseToE164Phone(raw);
  if (!e164) return null;

  if (e164.startsWith("+234") && e164.length >= 14) {
    const local = e164.slice(4);
    return `+234 ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }

  const digits = e164.slice(1);
  if (digits.length <= 3) return e164;
  return `+${digits.slice(0, 3)} ${digits.slice(3)}`;
};
